import { useEffect, useState } from 'react';

/**
 * App-wide text-size preference. Drives the root font-size via a CSS
 * `data-text-size` attribute on `<html>` so every Tailwind rem-based
 * utility (text-xs, text-sm, etc.) scales proportionally.
 *
 * Persistence model mirrors the sidebar toggle + i18next preference —
 * localStorage on first interaction, no server round-trip (a text-size
 * preference is per-device, not per-profile; a clinician on a 4K
 * monitor wants different sizing than the same clinician on a laptop).
 */

export type TextSize = 'small' | 'normal' | 'large' | 'x-large';

const STORAGE_KEY = 'ui.textSize';
const DEFAULT_SIZE: TextSize = 'normal';

const isTextSize = (v: unknown): v is TextSize =>
  v === 'small' || v === 'normal' || v === 'large' || v === 'x-large';

const readStored = (): TextSize => {
  if (typeof window === 'undefined') return DEFAULT_SIZE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isTextSize(raw)) return raw;
  } catch {
    /* private mode — fall through */
  }
  return DEFAULT_SIZE;
};

const writeStored = (value: TextSize) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
};

const applyToDocument = (value: TextSize) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.textSize = value;
};

export const useTextSize = (): [TextSize, (next: TextSize) => void] => {
  const [size, setSize] = useState<TextSize>(() => readStored());

  // Apply on mount AND whenever it changes so the document attribute
  // stays in sync across tabs (multiple components calling this hook
  // share the same DOM state via the attribute write).
  useEffect(() => {
    applyToDocument(size);
    writeStored(size);
  }, [size]);

  return [size, setSize];
};

/**
 * Apply the stored text size as early as possible. Call this from a
 * top-level component that mounts before any patient/dashboard content
 * so the scaling is in place before the first paint of large UI.
 */
export const useApplyStoredTextSize = (): void => {
  useEffect(() => {
    applyToDocument(readStored());
  }, []);
};
